import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useSpotifyStore } from './spotify'
import { useSpotifyNodesStore } from './spotifyNodes'
import { useMusicNodesStore, type MusicNode } from './musicNodes'
import { logger } from '@/lib/logger'

export interface PlayingMusicItem {
  id: string
  name: string
  type: 'music'
  volume: number
  loop: boolean
  audio: HTMLAudioElement
}

export interface PlayingSpotifyItem {
  id: string
  name: string
  type: 'spotify'
  volume: number
  imageUrl?: string
}

export type PlayingAudioItem = PlayingMusicItem | PlayingSpotifyItem

export const useAudioPlaybackStore = defineStore('audioPlayback', () => {
  const spotifyStore = useSpotifyStore()
  const spotifyNodesStore = useSpotifyNodesStore()
  const musicNodesStore = useMusicNodesStore()

  // State
  const playingMusicItems = ref<Map<string, PlayingMusicItem>>(new Map())
  const playingSpotifyItem = ref<PlayingSpotifyItem | null>(null)
  const isMuted = ref(false)
  const previousVolumes = ref<Map<string, number>>(new Map())

  // Getters
  const allPlayingItems = computed((): PlayingAudioItem[] => {
    const items: PlayingAudioItem[] = Array.from(playingMusicItems.value.values())
    if (playingSpotifyItem.value) {
      items.push(playingSpotifyItem.value)
    }
    return items
  })

  const hasPlayingAudio = computed(() => {
    return playingMusicItems.value.size > 0 || playingSpotifyItem.value !== null
  })

  const isSpotifyPlaying = computed(() => spotifyStore.isPlaying)

  // Actions
  const playMusic = async (node: MusicNode): Promise<boolean> => {
    if (!node.url) return false

    // Stop if already playing this one
    if (playingMusicItems.value.has(node.id)) {
      stopMusic(node.id)
      return false
    }

    try {
      const audio = new Audio(node.url)
      audio.volume = isMuted.value ? 0 : (node.volume || 1)
      audio.loop = node.loop !== false

      audio.onended = () => {
        if (!audio.loop) {
          playingMusicItems.value.delete(node.id)
        }
      }

      await audio.play()

      playingMusicItems.value.set(node.id, {
        id: node.id,
        name: node.name,
        type: 'music',
        volume: node.volume || 1,
        loop: node.loop !== false,
        audio
      })

      logger.info('▶️ Playing Music:', node.name)
      return true
    } catch (error) {
      logger.error('❌ Failed to play audio:', error)
      return false
    }
  }

  const stopMusic = (nodeId: string) => {
    const item = playingMusicItems.value.get(nodeId)
    if (item) {
      item.audio.pause()
      item.audio.currentTime = 0
      playingMusicItems.value.delete(nodeId)
      logger.info('⏹️ Stopped Music:', item.name)
    }
  }

  const stopAllMusic = () => {
    playingMusicItems.value.forEach((item) => {
      item.audio.pause()
      item.audio.currentTime = 0
    })
    playingMusicItems.value.clear()
    logger.info('⏹️ Stopped all music')
  }

  const playSpotify = async (nodeId: string): Promise<boolean> => {
    const node = spotifyNodesStore.getSpotifyNodeById(nodeId)
    if (!node?.spotify_url) return false

    try {
      const urlInfo = spotifyNodesStore.parseSpotifyUrl(node.spotify_url)
      if (!urlInfo) return false

      const spotifyUri = `spotify:${urlInfo.type}:${urlInfo.id}`

      // Set volume first
      if (node.volume !== 1) {
        await spotifyStore.setVolume(Math.round(node.volume * 100))
      }

      // Play the track/playlist/album
      await spotifyStore.play({
        context_uri: urlInfo.type !== 'track' ? spotifyUri : undefined,
        uris: urlInfo.type === 'track' ? [spotifyUri] : undefined
      })

      playingSpotifyItem.value = {
        id: node.id,
        name: node.name,
        type: 'spotify',
        volume: node.volume
      }

      logger.info('▶️ Playing Spotify:', node.name)
      return true
    } catch (error) {
      logger.error('❌ Failed to play Spotify:', error)
      return false
    }
  }

  const pauseSpotify = async () => {
    try {
      await spotifyStore.pause()
      logger.info('⏸️ Paused Spotify')
    } catch (error) {
      logger.error('❌ Failed to pause Spotify:', error)
    }
  }

  const stopSpotify = async () => {
    try {
      await spotifyStore.pause()
      playingSpotifyItem.value = null
      logger.info('⏹️ Stopped Spotify')
    } catch (error) {
      logger.error('❌ Failed to stop Spotify:', error)
    }
  }

  const nextTrack = async () => {
    try {
      await spotifyStore.nextTrack()
    } catch (error) {
      logger.error('❌ Failed to skip track:', error)
    }
  }

  const previousTrack = async () => {
    try {
      await spotifyStore.previousTrack()
    } catch (error) {
      logger.error('❌ Failed to go to previous track:', error)
    }
  }

  const stopAll = async () => {
    stopAllMusic()
    // Don't stop Spotify automatically - only stop via explicit user action
    logger.info('⏹️ Stopped all music audio')
  }

  const toggleMute = () => {
    isMuted.value = !isMuted.value
    
    // Apply mute to all playing music
    playingMusicItems.value.forEach((item) => {
      if (isMuted.value) {
        previousVolumes.value.set(item.id, item.audio.volume)
        item.audio.volume = 0
      } else {
        item.audio.volume = previousVolumes.value.get(item.id) || item.volume
      }
    })

    // Mute/unmute Spotify
    if (playingSpotifyItem.value) {
      if (isMuted.value) {
        spotifyStore.setVolume(0)
      } else {
        spotifyStore.setVolume(Math.round((playingSpotifyItem.value.volume || 1) * 100))
      }
    }

    logger.info(isMuted.value ? '🔇 Audio muted' : '🔊 Audio unmuted')
  }

  const setMusicVolume = (nodeId: string, volume: number) => {
    const item = playingMusicItems.value.get(nodeId)
    if (item) {
      item.volume = volume
      if (!isMuted.value) {
        item.audio.volume = volume
      }
    }
  }

  const setSpotifyVolume = async (volume: number) => {
    if (playingSpotifyItem.value) {
      playingSpotifyItem.value.volume = volume
      if (!isMuted.value) {
        await spotifyStore.setVolume(Math.round(volume * 100))
      }
    }
  }

  // Check if a specific music node is playing
  const isMusicPlaying = (nodeId: string) => {
    return playingMusicItems.value.has(nodeId)
  }

  return {
    // State
    playingMusicItems,
    playingSpotifyItem,
    isMuted,

    // Getters
    allPlayingItems,
    hasPlayingAudio,
    isSpotifyPlaying,

    // Actions
    playMusic,
    stopMusic,
    stopAllMusic,
    playSpotify,
    pauseSpotify,
    stopSpotify,
    nextTrack,
    previousTrack,
    stopAll,
    toggleMute,
    setMusicVolume,
    setSpotifyVolume,
    isMusicPlaying
  }
})
